import { useState, useEffect } from 'react'
import { FiX, FiAlertCircle, FiUpload, FiSun, FiHeart, FiCoffee, FiBriefcase, FiMoreHorizontal } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import CalendarioSelector from '../CalendarioSelector'
import { Btn } from '../../ui'

/*
 * SolicitudAusenciaModal (V2) — DS v2.
 * Mismas props y lógica que el legacy. Cambia visual: ui-card, tokens,
 * tipos como pills tonalizadas, Btn primitives.
 */

// Tipos con tonalidades del DS v2 (mismos colores conceptuales que el legacy)
const TIPOS_AUSENCIA = [
  { value: 'vacaciones',         label: 'Vacaciones',         icon: FiSun,             color: '#A86A06', bg: 'rgba(245,158,11,.06)',   border: 'rgba(245,158,11,.20)' },
  { value: 'baja_medica',        label: 'Baja médica',        icon: FiHeart,           color: '#C13030', bg: 'rgba(239,68,68,.06)',    border: 'rgba(239,68,68,.20)' },
  { value: 'permiso_retribuido', label: 'Permiso retribuido', icon: FiBriefcase,       color: '#2563EB', bg: 'rgba(37,99,235,.06)',    border: 'rgba(37,99,235,.20)' },
  { value: 'asuntos_propios',    label: 'Asuntos propios',    icon: FiCoffee,          color: '#0F8C66', bg: 'rgba(16,185,129,.06)',   border: 'rgba(16,185,129,.20)' },
  { value: 'otro',               label: 'Otro',               icon: FiMoreHorizontal,  color: 'var(--ink-2)', bg: 'var(--bg-2)',       border: 'var(--line)' }
]

const SolicitudAusenciaModalV2 = ({ ausencia, fechaSugerida, fechaSugeridaFin, onClose, onSuccess }) => {
  const [tipo, setTipo] = useState(ausencia?.tipo || 'vacaciones')
  const [fechaInicio, setFechaInicio] = useState(ausencia?.fecha_inicio || fechaSugerida || '')
  const [fechaFin, setFechaFin] = useState(ausencia?.fecha_fin || fechaSugeridaFin || fechaSugerida || '')
  const [motivo, setMotivo] = useState(ausencia?.motivo || '')
  const [documento, setDocumento] = useState(null)
  const [loading, setLoading] = useState(false)
  const [ausenciasExistentes, setAusenciasExistentes] = useState([])
  const [arrastrando, setArrastrando] = useState(false)

  // Punto 4.A — saldo de vacaciones + días que descuenta esta solicitud.
  const [saldo, setSaldo] = useState(null)
  const [diasSolicitud, setDiasSolicitud] = useState(null)
  const CONSUME_VACAS = ['vacaciones', 'dia_libre']
  const consumeVacaciones = CONSUME_VACAS.includes(tipo)

  const EXT_PERMITIDAS = ['pdf', 'jpg', 'jpeg', 'png']

  // Valida y asigna un archivo (usado tanto por el input como por drag & drop).
  const aceptarArchivo = (file) => {
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!EXT_PERMITIDAS.includes(ext)) {
      toast.error('Formato no válido. Adjunta un PDF, JPG o PNG.')
      return
    }
    setDocumento(file)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setArrastrando(false)
    const file = e.dataTransfer.files?.[0]
    aceptarArchivo(file)
  }

  useEffect(() => { cargarAusenciasExistentes() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar saldo cuando el tipo consume vacaciones.
  useEffect(() => {
    if (!consumeVacaciones) { setSaldo(null); return }
    const cargar = async () => {
      try {
        const token = localStorage.getItem('token')
        const anio = (fechaInicio || '').slice(0, 4) || new Date().getFullYear()
        const r = await fetch(`${API_URL}/ausencias/mi-saldo?anio=${anio}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (r.ok) setSaldo(await r.json())
      } catch { /* silencioso */ }
    }
    cargar()
  }, [consumeVacaciones, fechaInicio])

  // Calcular los días laborables que descontaría esta solicitud.
  useEffect(() => {
    if (!consumeVacaciones || !fechaInicio || !fechaFin) { setDiasSolicitud(null); return }
    let cancelado = false
    const calcular = async () => {
      try {
        const token = localStorage.getItem('token')
        const r = await fetch(`${API_URL}/ausencias/calcular-dias?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (r.ok && !cancelado) { const d = await r.json(); setDiasSolicitud(d.dias) }
      } catch { /* silencioso */ }
    }
    calcular()
    return () => { cancelado = true }
  }, [consumeVacaciones, fechaInicio, fechaFin])

  const cargarAusenciasExistentes = async () => {
    try {
      const token = localStorage.getItem('token')
      const anio = new Date().getFullYear()
      const r = await fetch(`${API_URL}/ausencias/mis-ausencias?anio=${anio}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (r.ok) {
        const data = await r.json()
        setAusenciasExistentes(data.filter(a => a.estado !== 'rechazada' && (!ausencia || a.id !== ausencia.id)))
      }
    } catch { /* silencioso */ }
  }

  const handleSeleccionFechas = (inicio, fin) => {
    setFechaInicio(inicio)
    setFechaFin(fin)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!fechaInicio || !fechaFin) { toast.error('Selecciona las fechas en el calendario'); return }
    if (!motivo.trim()) { toast.error('Indica el motivo de la ausencia'); return }
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('tipo', tipo)
      formData.append('fecha_inicio', fechaInicio)
      formData.append('fecha_fin', fechaFin)
      formData.append('motivo', motivo)
      if (documento) formData.append('documento', documento)
      const url = ausencia ? `${API_URL}/ausencias/${ausencia.id}` : `${API_URL}/ausencias`
      const response = await fetch(url, {
        method: ausencia ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Error al enviar solicitud')
      toast.success(ausencia ? 'Ausencia actualizada' : 'Solicitud enviada correctamente')
      onSuccess()
    } catch (error) {
      toast.error(error.message || 'Error al procesar la solicitud')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-md max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)] sticky top-0 bg-[var(--bg)] z-10">
          <div>
            <div className="eyebrow mb-1">{ausencia ? 'Editar' : 'Nueva'}</div>
            <h3 className="font-display text-[22px] text-[var(--ink)] leading-tight">
              {ausencia ? 'Editar solicitud.' : 'Solicitar permiso.'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Tipo */}
          <div>
            <label className="eyebrow block mb-2">Tipo de ausencia</label>
            <div className="grid grid-cols-2 gap-2">
              {TIPOS_AUSENCIA.map(t => {
                const Icon = t.icon
                const isActive = tipo === t.value
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTipo(t.value)}
                    className="flex items-center gap-2 p-2.5 rounded-soft border text-left text-[12.5px] font-medium transition-all"
                    style={{
                      background: isActive ? t.bg : 'var(--bg-2)',
                      borderColor: isActive ? t.color : 'var(--line)',
                      color: isActive ? t.color : 'var(--ink-2)',
                      boxShadow: isActive ? `0 0 0 2px ${t.bg}` : 'none'
                    }}
                  >
                    <Icon size={14} className="flex-shrink-0" />
                    <span className="truncate">{t.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Calendario */}
          <div>
            <label className="eyebrow block mb-2">Fechas</label>
            <div className="border border-[var(--line)] rounded-soft p-3 bg-[var(--bg-2)]">
              <CalendarioSelector
                fechaInicio={fechaInicio}
                fechaFin={fechaFin}
                onSeleccion={handleSeleccionFechas}
                ausenciasExistentes={ausenciasExistentes}
              />
            </div>
          </div>

          {/* Saldo de vacaciones (Punto 4.A) */}
          {consumeVacaciones && saldo && (
            <div className="p-3 rounded-soft bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
              <div className="flex items-center justify-between">
                <span className="text-[var(--ink-3)]">Te quedan</span>
                <span className={`font-mono tnum font-semibold ${saldo.dias_restantes < 0 ? 'text-[#C13030]' : 'text-[var(--ink)]'}`}>
                  {saldo.dias_restantes} de {saldo.dias_anuales} días
                </span>
              </div>
              {diasSolicitud != null && (
                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-[var(--line)]">
                  <span className="text-[var(--ink-3)]">Esta solicitud descuenta</span>
                  <span className="font-mono tnum font-medium text-[var(--ink)]">{diasSolicitud} día{diasSolicitud === 1 ? '' : 's'}</span>
                </div>
              )}
              {diasSolicitud != null && diasSolicitud > saldo.dias_restantes && (
                <p className="text-[11px] text-[#A86A06] mt-1.5 flex items-start gap-1">
                  <FiAlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  Esta solicitud supera tu saldo restante. Puedes enviarla igualmente; el administrador decidirá.
                </p>
              )}
            </div>
          )}

          {/* Motivo */}
          <div>
            <label className="eyebrow block mb-1.5">Motivo</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[var(--ink-3)]"
              placeholder="Describe brevemente el motivo…"
              required
            />
          </div>

          {/* Documento */}
          <div>
            <label className="eyebrow block mb-1.5">
              Documento adjunto <span className="text-[var(--ink-3)] font-normal normal-case tracking-normal">(opcional)</span>
            </label>
            <label
              onDragOver={e => { e.preventDefault(); setArrastrando(true) }}
              onDragLeave={e => { e.preventDefault(); setArrastrando(false) }}
              onDrop={onDrop}
              className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-soft cursor-pointer transition-colors ${arrastrando ? 'border-[var(--ink-3)] bg-[var(--bg-2)]' : 'border-[var(--line)] hover:border-[var(--ink-3)] hover:bg-[var(--bg-2)]'}`}
            >
              <FiUpload className="text-[var(--ink-3)]" size={16} />
              <span className="text-[13px] text-[var(--ink-3)]">
                {documento ? documento.name : (arrastrando ? 'Suelta el archivo aquí' : 'Subir archivo o arrastrarlo aquí (PDF, JPG, PNG)')}
              </span>
              <input
                type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => aceptarArchivo(e.target.files[0])}
              />
            </label>
            {documento && (
              <button
                type="button"
                onClick={() => setDocumento(null)}
                className="text-[11px] text-[#C13030] hover:opacity-80 mt-1"
              >
                Quitar archivo
              </button>
            )}
            {tipo === 'baja_medica' && (
              <p className="text-[11px] text-[#A86A06] mt-1">
                El administrador podrá solicitar documentación justificativa para las bajas médicas.
              </p>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-soft bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
            <FiAlertCircle className="text-[var(--brand-1)] mt-0.5 flex-shrink-0" size={14} />
            <p className="text-[11.5px] text-[var(--brand-1)]">
              Tu solicitud será revisada por un administrador. Recibirás una notificación con la respuesta.
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-3">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={loading}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={loading || !fechaInicio}>
              {loading ? 'Enviando…' : ausencia ? 'Actualizar' : 'Enviar solicitud'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SolicitudAusenciaModalV2
